
import { useState, FormEvent } from 'react';

export default function RequestQuote() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    projectType: '',
    additionalInfo: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.email) return;
    if (formData.additionalInfo.length > 500) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        email: formData.email,
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        Phone: formData.phone,
        'Project Type': formData.projectType,
        'Project Details': formData.additionalInfo,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6bk4piff40lgbk6cfig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', projectType: '', additionalInfo: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Structural Steel Welding',
    'Custom Metal Fabrication',
    'Mobile Welding Repair',
    'Heavy Equipment Repair',
    'CNC Plasma / Torch Cutting',
    'Custom Gates & Fencing',
    'AG Equipment',
    'Staircases & Handrails',
    'Ornamental Ironwork',
    'Other',
  ];

  return (
    <section id="request-quote" className="py-24 bg-[#F8F7F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-[#E8740C]"></div>
              <span className="text-[#E8740C] text-xs font-semibold tracking-[0.2em] font-heading uppercase">Get Started</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6">
              REQUEST A<br /><span className="text-[#E8740C]">FREE QUOTE</span>
            </h2>
            <p className="text-[#4A4A4A] text-sm leading-relaxed mb-8 font-body">
              Tell us about your welding or fabrication project and we&apos;ll provide a detailed, no-obligation estimate. Most quotes returned within 24 hours.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/10 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-phone-fill text-[#E8740C] text-lg"></i>
                </div>
                <div>
                  <p className="text-[#1A1A1A] font-heading font-bold text-sm">Call Us Direct</p>
                  <a href="tel:5308068659" className="text-[#E8740C] text-sm font-medium cursor-pointer hover:underline">530-806-8659</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/10 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-time-fill text-[#E8740C] text-lg"></i>
                </div>
                <div>
                  <p className="text-[#1A1A1A] font-heading font-bold text-sm">24/7 Availability</p>
                  <p className="text-[#6A6A6A] text-sm">Emergency welding repair — any time, any day</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8740C]/10 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-fill text-[#E8740C] text-lg"></i>
                </div>
                <div>
                  <p className="text-[#1A1A1A] font-heading font-bold text-sm">Service Area</p>
                  <p className="text-[#6A6A6A] text-sm">Yuba County &amp; all of Northern &amp; Southern California</p>
                </div>
              </div>
            </div>
          </div>

          <form
            id="contractor-quote-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-lg p-8 shadow-lg border border-[#E8E6E2]"
          >
            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label htmlFor="firstName" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent"
                  placeholder="Your first name"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent"
                  placeholder="Your last name"
                />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="email" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="phone" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="projectType" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                Project Type
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent bg-white cursor-pointer"
              >
                <option value="">Select a service...</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="additionalInfo" className="block text-[#1A1A1A] text-xs font-semibold mb-1.5">
                Project Details
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={4}
                maxLength={500}
                value={formData.additionalInfo}
                onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                className="w-full px-4 py-3 border border-[#D4D4D4] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#E8740C] focus:border-transparent resize-none"
                placeholder="Describe your project — materials, dimensions, timeline, location..."
              />
              <p className="text-[#9A9A9A] text-xs mt-1">{formData.additionalInfo.length}/500</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || formData.additionalInfo.length > 500}
              className="w-full bg-[#E8740C] text-white font-heading font-bold text-sm tracking-wider py-4 rounded-md hover:bg-[#d16a0b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'SUBMITTING...' : 'SUBMIT QUOTE REQUEST'}
            </button>

            {submitStatus === 'success' && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4 text-center">
                <p className="text-green-700 text-sm font-medium">Quote request submitted! We&apos;ll be in touch within 24 hours.</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4 text-center">
                <p className="text-red-700 text-sm font-medium">Something went wrong. Please try again or call us directly.</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
